// src/components/SignUp.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/signup.css";
import perro from "../assets/perro_cc.webp";
import { X, CheckCircle2, Home as HomeIcon } from "lucide-react";
import useBackend from "../hooks/useBackend";
import config from "../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

// ✅ SOLO PARA PRUEBA TEMPORAL DE EPAYCO
const FORCE_COUNTRY_FOR_TEST = ""; // usa "EC" para probar ePayco, luego déjalo en ""

// ✅ Códigos válidos de aliados
const VALID_INFLUENCER_CODES = new Set([
  "LARY",
  "LUISANOMADA",
  "AFGF",
  "SGF1",
  "CHIGUIRO",
  "HUESOS",
  "RG6",
  "PANCHAUX",
  "LAUHOLAHOLA",
  "VANE08",
  "DANNAFAM",
  "JUANPAPET",
  "MELIPETFAM",
  "SILENY",
]);

function normalizeInfluencerCode(raw) {
  return String(raw || "").trim().toUpperCase().replace(/\s+/g, "");
}

function getInfluencerCodeFromUrl(search) {
  const params = new URLSearchParams(search || "");
  const raw =
    params.get("ref") ||
    params.get("codigo") ||
    params.get("code") ||
    "";
  const normalized = normalizeInfluencerCode(raw);
  return VALID_INFLUENCER_CODES.has(normalized) ? normalized : "";
}

async function fetchFirstOkJson(urls, options) {
  let lastErr = null;

  for (const u of urls) {
    try {
      const r = await fetch(u, options);
      if (!r.ok) {
        lastErr = new Error(`HTTP ${r.status} en ${u}`);
        continue;
      }
      const data = await r.json().catch(() => null);
      return { ok: true, url: u, data };
    } catch (e) {
      lastErr = e;
    }
  }

  return { ok: false, error: lastErr || new Error("No hubo respuesta válida.") };
}

const COMMON_EMAIL_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "yahoo.com",
  "msn.com",
  "hotmail.es",
  "outlook.es",
];

const EMAIL_DOMAIN_FIXES = {
  "gmal.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.cm": "gmail.com",

  "hotmai.com": "hotmail.com",
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotnail.com": "hotmail.com",

  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "otulook.com": "outlook.com",
  "outlook.co": "outlook.com",
  "outlook.con": "outlook.com",

  "yaho.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "yahoo.con": "yahoo.com",

  "iclod.com": "icloud.com",
  "icloud.co": "icloud.com",
  "icloud.con": "icloud.com",

  "live.co": "live.com",
  "live.con": "live.com",

  "msn.co": "msn.com",
  "msn.con": "msn.com",

  "hotmail.com.co": "hotmail.com",
  "gmail.com.co": "gmail.com",
  "outlook.com.co": "outlook.com",
};

function levenshtein(a, b) {
  const s = String(a || "");
  const t = String(b || "");

  const rows = s.length + 1;
  const cols = t.length + 1;
  const dp = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let i = 0; i < rows; i++) dp[i][0] = i;
  for (let j = 0; j < cols; j++) dp[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }

  return dp[s.length][t.length];
}

function normalizeEmailInput(value) {
  return String(value || "").trim().toLowerCase();
}

function getEmailSuggestion(email) {
  const normalized = normalizeEmailInput(email);
  if (!normalized || !normalized.includes("@")) return "";

  const parts = normalized.split("@");
  if (parts.length !== 2) return "";

  const localPart = parts[0];
  const domain = parts[1];

  if (!localPart || !domain) return "";

  if (EMAIL_DOMAIN_FIXES[domain]) {
    return `${localPart}@${EMAIL_DOMAIN_FIXES[domain]}`;
  }

  let bestMatch = "";
  let bestDistance = Infinity;

  for (const candidate of COMMON_EMAIL_DOMAINS) {
    const dist = levenshtein(domain, candidate);
    if (dist < bestDistance) {
      bestDistance = dist;
      bestMatch = candidate;
    }
  }

  if (bestMatch && bestDistance > 0 && bestDistance <= 2) {
    return `${localPart}@${bestMatch}`;
  }

  return "";
}

export default function SignUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { post, loading } = useBackend();

  const t = location.state?.transition;
  const screenUp = t === "screen" || t === "screen-up" || t === "up";
  const screenDown = t === "screen-down" || t === "down";

  const [enterClass, setEnterClass] = useState(
    screenDown ? "is-entering-down" : screenUp ? "is-entering" : ""
  );

  useEffect(() => {
    if (screenUp || screenDown) {
      const id = setTimeout(() => setEnterClass(""), 420);
      return () => clearTimeout(id);
    }
  }, [screenUp, screenDown]);

  useEffect(() => {
    const from = location.state?.from;
    const backTo = location.state?.backTo;

    if (typeof backTo === "string" && backTo.trim()) {
      sessionStorage.setItem("pf_signup_from", backTo.trim());
      return;
    }
    if (typeof from === "string" && from.trim()) {
      sessionStorage.setItem("pf_signup_from", from.trim());
    }
  }, [location.state]);

  // ✅ Leer código desde la URL y guardarlo si es válido
  useEffect(() => {
    const refCode = getInfluencerCodeFromUrl(location.search);

    if (refCode) {
      localStorage.setItem("petfam_influencer_code", refCode);
      sessionStorage.setItem("petfam_influencer_code", refCode);
      return;
    }

    const stored = normalizeInfluencerCode(
      localStorage.getItem("petfam_influencer_code") ||
      sessionStorage.getItem("petfam_influencer_code") ||
      ""
    );

    if (stored && !VALID_INFLUENCER_CODES.has(stored)) {
      localStorage.removeItem("petfam_influencer_code");
      sessionStorage.removeItem("petfam_influencer_code");
    }
  }, [location.search]);

  const influencerCode = useMemo(() => {
    const fromUrl = getInfluencerCodeFromUrl(location.search);
    if (fromUrl) return fromUrl;

    const stored = normalizeInfluencerCode(
      localStorage.getItem("petfam_influencer_code") ||
      sessionStorage.getItem("petfam_influencer_code") ||
      ""
    );

    return VALID_INFLUENCER_CODES.has(stored) ? stored : "";
  }, [location.search]);

  const MAX_CEDULA_DIGITS = 10;
  const MAX_PHONE_DIGITS = 10;
  const MAX_PASSPORT_LEN = 15;

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    tipodoc: "",
    dni: "",
    email: "",
    telefono: "",
    paisResidencia: "",
  });

  const PLAN_ID = "mensual";

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [showSubmitting, setShowSubmitting] = useState(false);

  const [geoCountry, setGeoCountry] = useState("");
  const [geoLoading, setGeoLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        setGeoLoading(true);

        if (FORCE_COUNTRY_FOR_TEST === "EC" || FORCE_COUNTRY_FOR_TEST === "CO") {
          setGeoCountry(FORCE_COUNTRY_FOR_TEST);
          setFormData((prev) => ({ ...prev, paisResidencia: FORCE_COUNTRY_FOR_TEST }));
          setErrors((prev) => ({ ...prev, paisResidencia: "" }));
          return;
        }

        const urls = [`${API_BASE_URL}/geo/country`, `${API_BASE_URL}/api/geo/country`];

        const res = await fetchFirstOkJson(urls, { cache: "no-store" });
        if (!res.ok) {
          setGeoCountry("UN");
          return;
        }

        const c = String(res.data?.country || "UN").toUpperCase();
        const normalized = c === "CO" || c === "EC" ? c : "UN";
        setGeoCountry(normalized);

        if (normalized === "CO" || normalized === "EC") {
          setFormData((prev) => ({ ...prev, paisResidencia: normalized }));
          setErrors((prev) => ({ ...prev, paisResidencia: "" }));
        }
      } catch {
        setGeoCountry("UN");
      } finally {
        setGeoLoading(false);
      }
    };

    run();
  }, []);

  const isAutoCountry =
    FORCE_COUNTRY_FOR_TEST === "EC" ||
    FORCE_COUNTRY_FOR_TEST === "CO" ||
    geoCountry === "CO" ||
    geoCountry === "EC";

  const normalizeNameInput = (val) => {
    const up = String(val ?? "").toUpperCase();
    return up.replace(/\s{2,}/g, " ");
  };

  const onlyDigits = (val) => String(val || "").replace(/\D/g, "");

  const normalizePassport = (val) => {
    return String(val || "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/[^A-Z0-9\-]/g, "")
      .slice(0, MAX_PASSPORT_LEN);
  };

  const isValidCedulaEcuador = (cedula) => {
    const c = String(cedula || "").replace(/\D/g, "");
    if (c.length !== 10) return false;

    const provincia = parseInt(c.slice(0, 2), 10);
    if (Number.isNaN(provincia) || provincia < 1 || provincia > 24) return false;

    const tercer = parseInt(c[2], 10);
    if (Number.isNaN(tercer) || tercer < 0 || tercer > 5) return false;

    const digitos = c.split("").map((x) => parseInt(x, 10));
    const verificador = digitos[9];

    const coef = [2, 1, 2, 1, 2, 1, 2, 1, 2];
    let suma = 0;

    for (let i = 0; i < 9; i++) {
      let prod = digitos[i] * coef[i];
      if (prod >= 10) prod -= 9;
      suma += prod;
    }

    const mod = suma % 10;
    const check = mod === 0 ? 0 : 10 - mod;

    return check === verificador;
  };

  const emailSuggestion = useMemo(() => {
    const raw = formData.email;
    if (!raw || !raw.includes("@")) return "";
    const suggestion = getEmailSuggestion(raw);
    const normalized = normalizeEmailInput(raw);
    return suggestion && suggestion !== normalized ? suggestion : "";
  }, [formData.email]);

  const validateField = (name, value, data) => {
    const v = String(value ?? "").trim();
    const d = data || formData;

    const requiredFields = [
      "nombre",
      "apellido",
      "tipodoc",
      "dni",
      "email",
      "telefono",
      "paisResidencia",
    ];

    if (requiredFields.includes(name) && !v) {
      return "Este campo es obligatorio.";
    }

    if (name === "nombre") {
      if (v.length > 0 && v.length < 3) return "Debe tener mínimo 3 letras.";
      if (v && !/^[A-ZÁÉÍÓÚÜÑ ]+$/.test(v)) return "Este campo sólo permite letras.";
      return "";
    }

    if (name === "apellido") {
      if (v.length > 0 && v.length < 3) return "Debe tener mínimo 3 letras.";
      if (v && !/^[A-ZÁÉÍÓÚÜÑ ]+$/.test(v)) return "Este campo sólo permite letras.";
      return "";
    }

    if (name === "tipodoc") {
      if (!v) return "Debes seleccionar un tipo de documento.";
      return "";
    }

    if (name === "dni") {
      const tipo = d.tipodoc;

      if (!tipo) return "Primero selecciona el tipo de documento.";

      if (tipo === "cc") {
        const digits = onlyDigits(v);
        if (!digits) return "Este campo es obligatorio.";
        if (digits.length > MAX_CEDULA_DIGITS) return `Máximo ${MAX_CEDULA_DIGITS} dígitos.`;
        if (digits.length < 5) return "Número inválido.";

        if (d.paisResidencia === "EC") {
          if (digits.length !== 10) return "En Ecuador la cédula debe tener 10 dígitos.";
          if (!isValidCedulaEcuador(digits)) return "Cédula ecuatoriana inválida.";
        }

        return "";
      }

      if (tipo === "pasaporte") {
        const compact = v.replace(/\s/g, "");
        if (!compact) return "Este campo es obligatorio.";
        if (compact.length > MAX_PASSPORT_LEN) return `Máximo ${MAX_PASSPORT_LEN} caracteres.`;
        if (!/^[A-Z0-9\-]+$/i.test(compact)) return "Usa solo letras, números y guion (-).";
        if (!/[A-Z]/i.test(compact)) return "El pasaporte debe contener al menos una letra.";
        return "";
      }

      return "";
    }

    if (name === "email") {
      const normalizedEmail = normalizeEmailInput(v);

      if (!normalizedEmail) return "Este campo es obligatorio.";
      if (!/^\S+@\S+\.\S+$/.test(normalizedEmail)) return "Usa el formato correo@ejemplo.com.";

      return "";
    }

    if (name === "telefono") {
      const digits = onlyDigits(v);
      if (!digits) return "Este campo es obligatorio.";
      if (digits.length > MAX_PHONE_DIGITS) return `Máximo ${MAX_PHONE_DIGITS} dígitos.`;

      if (d.paisResidencia === "CO") {
        if (digits.length !== 10) return "En Colombia debe tener 10 dígitos.";
      } else if (d.paisResidencia === "EC") {
        if (digits.length !== 10) return "En Ecuador debe tener 10 dígitos.";
      } else {
        if (digits.length < 7) return "Número inválido.";
      }

      return "";
    }

    if (name === "paisResidencia") {
      if (!v) return "Selecciona tu país de residencia.";
      if (v !== "CO" && v !== "EC") return "País inválido.";
      return "";
    }

    return "";
  };

  const validateForm = (data) => {
    const fields = ["nombre", "apellido", "tipodoc", "dni", "email", "telefono", "paisResidencia"];
    const newErrors = {};
    fields.forEach((f) => {
      const msg = validateField(f, data[f], data);
      if (msg) newErrors[f] = msg;
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    setFormData((prevState) => {
      let nextValue = value;

      if (name === "nombre" || name === "apellido") {
        nextValue = normalizeNameInput(nextValue);
      }

      if (name === "dni") {
        const tipo = prevState.tipodoc;
        if (tipo === "cc") {
          nextValue = onlyDigits(nextValue).slice(0, MAX_CEDULA_DIGITS);
        } else if (tipo === "pasaporte") {
          nextValue = String(nextValue || "")
            .toUpperCase()
            .replace(/[^A-Z0-9\- ]/g, "")
            .replace(/\s{2,}/g, " ")
            .slice(0, MAX_PASSPORT_LEN);
        } else {
          nextValue = String(nextValue || "");
        }
      }

      if (name === "telefono") {
        nextValue = onlyDigits(nextValue).slice(0, MAX_PHONE_DIGITS);
      }

      if (name === "email") {
        nextValue = String(nextValue || "").replace(/\s+/g, "").toLowerCase();
      }

      const nextData = { ...prevState, [name]: nextValue };

      const msg = validateField(name, nextValue, nextData);
      setErrors((prev) => ({ ...prev, [name]: msg }));

      if (name === "tipodoc") {
        const msgDni = validateField("dni", nextData.dni, nextData);
        setErrors((prev) => ({ ...prev, dni: msgDni }));
      }

      if (name === "paisResidencia") {
        const msgDni = validateField("dni", nextData.dni, nextData);
        const msgTel = validateField("telefono", nextData.telefono, nextData);
        setErrors((prev) => ({ ...prev, dni: msgDni, telefono: msgTel }));
      }

      return nextData;
    });

    setSubmitError("");
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    setFormData((prev) => {
      const msg = validateField(name, prev[name], prev);
      setErrors((p) => ({ ...p, [name]: msg }));
      return prev;
    });
  };

  const applyEmailSuggestion = () => {
    if (!emailSuggestion) return;

    setFormData((prev) => ({ ...prev, email: emailSuggestion }));
    setTouched((prev) => ({ ...prev, email: true }));
    setErrors((prev) => ({ ...prev, email: "" }));
    setSubmitError("");
  };

  const handleClose = (e) => {
    e.preventDefault();

    const explicit = (location.state?.backTo || "").trim().toLowerCase();
    if (explicit === "/servicios") {
      navigate("/servicios", { state: { transition: "down" } });
      return;
    }
    if (explicit === "/" || explicit === "/home") {
      navigate("/", { state: { transition: "down" } });
      return;
    }

    const fromState = (location.state?.from || "").trim().toLowerCase();
    const fromStored = (sessionStorage.getItem("pf_signup_from") || "").trim().toLowerCase();
    const from = fromState || fromStored;

    if (from === "servicios" || from === "/servicios") {
      navigate("/servicios", { state: { transition: "down" } });
    } else {
      navigate("/", { state: { transition: "down" } });
    }
  };

  const SIGNUP_ENDPOINT = "/user/signup";

  const handleRegistrarme = async () => {
    setTouched({
      nombre: true,
      apellido: true,
      tipodoc: true,
      dni: true,
      email: true,
      telefono: true,
      paisResidencia: true,
    });

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    setSubmitError("");
    setShowSubmitting(true);

    const draft = {
      planId: PLAN_ID,
      influencerCode,
      customer: {
        country: formData.paisResidencia,
        name: formData.nombre.trim(),
        lastName: formData.apellido.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.telefono.trim(),
        dni: formData.dni.trim(),
        tipoDoc: formData.tipodoc,
      },
      createdAt: new Date().toISOString(),
    };

    const documentType = draft.customer.tipoDoc === "cc" ? "CC" : "PASSPORT";

    const dniForBackend =
      documentType === "CC" ? onlyDigits(draft.customer.dni) : normalizePassport(draft.customer.dni);

    const payload = {
      name: draft.customer.name,
      lastName: draft.customer.lastName,
      phone: draft.customer.phone,
      email: draft.customer.email,
      dni: dniForBackend,
      documentType,
      password: dniForBackend,
      status: "PENDING_PAYMENT",
      country: draft.customer.country,
    };

    try {
      const resp = await post(SIGNUP_ENDPOINT, null, payload);

      const createdUser = resp?.data ?? resp;
      const idUser = createdUser?.idUser || createdUser?.id || null;

      localStorage.setItem("petfam_signup_draft", JSON.stringify(draft));
      localStorage.setItem("petfam_selected_plan", PLAN_ID);
      localStorage.setItem("petfam_customer_email", draft.customer.email);
      localStorage.setItem("userEmail", draft.customer.email);
      if (influencerCode) {
        localStorage.setItem("petfam_influencer_code", influencerCode);
        sessionStorage.setItem("petfam_influencer_code", influencerCode);
      }
      if (idUser != null) localStorage.setItem("idUser", String(idUser));

      sessionStorage.setItem("pf_signup_from", "signup");

      const status = String(createdUser?.status || "").toUpperCase();
      const subActive = createdUser?.subscriptionActive;

      if (status === "ACTIVE" && subActive === true) {
        setShowSubmitting(false);
        navigate("/login", {
          state: {
            transition: "up",
            from: "/crear-cuenta",
            prefillEmail: draft.customer.email,
          },
        });
        return;
      }

      const selectedCountry = String(draft.customer.country || "").toUpperCase();
      const paymentRoute = selectedCountry === "EC" ? "/pago-epayco" : "/pago-wompi";

      navigate(paymentRoute, {
        state: {
          from: "signup",
          transition: "up",
          planId: PLAN_ID,
          email: draft.customer.email,
          signupDraft: draft,
          backendUser: createdUser,
          countryCode: selectedCountry,
          paymentProvider: selectedCountry === "EC" ? "EPAYCO" : "WOMPI",
          currency: selectedCountry === "EC" ? "USD" : "COP",
          influencerCode,
        },
      });
    } catch (e) {
      setSubmitError(e?.message || "Ocurrió un error creando el usuario.");
      setShowSubmitting(false);
    }
  };

  const showError = (field) => touched[field] && errors[field];

  return (
    <div className={`dd-shell ${enterClass}`}>
      {showSubmitting ? (
        <div className="pf-modal-overlay" role="dialog" aria-modal="true" aria-label="Registrándome">
          <div className="pf-modal">
            <p className="pf-modal-title">Registrándome...</p>
            <p className="pf-modal-subtitle">Por favor espera un momento.</p>
          </div>
        </div>
      ) : null}

      <a
        href="#close"
        onClick={handleClose}
        className="signup-close signup-close--left"
        aria-label="Volver"
        title="Volver"
      >
        <X size={18} aria-hidden="true" />
      </a>

      <Link to="/" className="signup-home" aria-label="Ir al inicio" title="Inicio">
        <HomeIcon size={18} aria-hidden="true" />
      </Link>

      <header className="dd-header dd-header--simple"></header>

      <div className="dd-hero page-enter">
        <aside className="dd-left">
          <div className="dd-figure">
            <img src={perro} alt="Dog hero" loading="lazy" />
          </div>
        </aside>

        <section className="dd-right">
          <div className="dd-copy">

  <h1>Vamos a registrarte en PETFAM</h1>
  <p>Ingresa tu información a continuación</p>
            {influencerCode ? (
              <p
                style={{
                  marginTop: "8px",
                  marginBottom: 0,
                  fontSize: "13px",
                  color: "#0B2F3E",
                  fontWeight: 600,
                }}
              >
                Estás ingresando con el código: {influencerCode}
              </p>
            ) : null}
          </div>

          <div className="dd-card">
             <span
    style={{
      display: "block",
      marginBottom: "14px",
      fontSize: "15px",
      fontWeight: 700,
      color: "#0B2F3E",
    }}
  >
    Paso 1 de 2
  </span>
            <div className="plan-block">
              <div className="plan-option plan-option--active">
                <div className="plan-left">
                  <div className="plan-badge" aria-hidden="true">
                    <CheckCircle2 size={18} />
                  </div>
                  <div className="plan-text">
                    <strong>Suscripción mensual</strong>
                  </div>
                </div>
              </div>
            </div>

            <h2>Los campos con * son obligatorios</h2>

            {submitError ? (
              <p className="error-msg" style={{ marginTop: 8 }}>
                {submitError}
              </p>
            ) : null}

            <form onSubmit={(e) => e.preventDefault()} className="dd-form">
              <label>
                <span>Nombre*</span>
                <input
                  type="text"
                  placeholder="Tu nombre"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  autoComplete="given-name"
                />
                {showError("nombre") ? <p className="error-msg">{errors.nombre}</p> : null}
              </label>

              <label>
                <span>Apellido*</span>
                <input
                  type="text"
                  placeholder="Tu apellido"
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  autoComplete="family-name"
                />
                {showError("apellido") ? <p className="error-msg">{errors.apellido}</p> : null}
              </label>

              {!isAutoCountry ? (
                <label>
                  <span>País de residencia*</span>
                  <select
                    id="paisResidencia"
                    name="paisResidencia"
                    value={formData.paisResidencia}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading || showSubmitting || geoLoading}
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="CO">COLOMBIA</option>
                    <option value="EC">ECUADOR</option>
                  </select>
                  {showError("paisResidencia") ? (
                    <p className="error-msg">{errors.paisResidencia}</p>
                  ) : null}
                </label>
              ) : null}

              <label>
                <span>Tipo de documento*</span>
                <select
                  id="tipodoc"
                  name="tipodoc"
                  value={formData.tipodoc}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                >
                  <option value="">Selecciona una opción</option>
                  <option value="cc">CÉDULA DE CIUDADANÍA</option>
                  <option value="pasaporte">PASAPORTE</option>
                </select>
                {showError("tipodoc") ? <p className="error-msg">{errors.tipodoc}</p> : null}
              </label>

              <label>
                <span>Número de identidad*</span>
                <input
                  type="text"
                  placeholder={
                    formData.tipodoc === "pasaporte" ? "Tu pasaporte" : "Tu número de documento"
                  }
                  id="dni"
                  name="dni"
                  value={formData.dni}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  inputMode={formData.tipodoc === "cc" ? "numeric" : "text"}
                  maxLength={formData.tipodoc === "pasaporte" ? MAX_PASSPORT_LEN : MAX_CEDULA_DIGITS}
                />
                {showError("dni") ? <p className="error-msg">{errors.dni}</p> : null}
              </label>

              <label>
                <span>Email*</span>
                <input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  autoComplete="email"
                />

                <p
                  style={{
                    marginTop: "6px",
                    marginBottom: 0,
                    fontSize: "12px",
                    lineHeight: 1.4,
                    color: "#6b7280",
                  }}
                >
                  A este correo enviaremos tus credenciales de acceso. Verifica que esté escrito
                  correctamente.
                </p>

                {emailSuggestion && touched.email ? (
                  <p
                    style={{
                      marginTop: "6px",
                      marginBottom: 0,
                      fontSize: "12px",
                      lineHeight: 1.4,
                      color: "#0B2F3E",
                    }}
                  >
                    ¿Quisiste decir{" "}
                    <button
                      type="button"
                      onClick={applyEmailSuggestion}
                      disabled={loading || showSubmitting}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        color: "inherit",
                        textDecoration: "underline",
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                ) : null}

                {showError("email") ? <p className="error-msg">{errors.email}</p> : null}
              </label>

              <label>
                <span>Celular*</span>
                <input
                  type="text"
                  placeholder="Número de contacto"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  inputMode="numeric"
                  maxLength={MAX_PHONE_DIGITS}
                />
                {showError("telefono") ? <p className="error-msg">{errors.telefono}</p> : null}
              </label>

              <button
                type="button"
                className="pill dark"
                onClick={handleRegistrarme}
                disabled={loading || showSubmitting}
              >
                Registrarme
              </button>

              <p className="dd-alt">
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" state={{ from: "/crear-cuenta" }}>
                  Inicia sesión
                </Link>
                <img
                  src={require("../assets/gatoCC.webp")}
                  alt="Gato señalando"
                  className="gato-img"
                />
              </p>
            </form>
          </div>
        </section>
      </div>
      <a
  href="https://wa.me/573001234567?text=¡Hola!%20Quiero%20cuidar%20a%20mi%20mascota%20con%20los%20mejores%20servicios%20de%20Petfam%20🐾"
  target="_blank"
  rel="noopener noreferrer"
  className="whatsapp-float"
  aria-label="WhatsApp Petfam"
>
  <img
    src={require("../assets/whatsapp.png")}
    alt="WhatsApp"
  />
</a>
    </div>
  );
}